import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/**
 * A real React error boundary.
 *
 * React has no hook form of this: catching a render error requires a class with
 * getDerivedStateFromError, and without one an uncaught error anywhere in the tree unmounts the
 * whole root. That is not a theoretical failure. Two undefined fields reaching titleCase and
 * StatusPill took the entire operator console to a blank page, navigation included, because there
 * was nothing between the throwing component and the root.
 *
 * The names SurfaceBoundary and ApplicationBoundary in app/App.tsx sound like this and are not:
 * both are routing guards that redirect, neither catches anything.
 *
 * Placement is the whole point. A boundary wrapped around the shell would still take the navigation
 * down with the page, so the one that matters sits inside the shell around the outlet: the sidebar,
 * the topbar and the workspace switch survive, and only the pane that failed is replaced.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Changing this clears a caught error.
   *
   * Without it a boundary latches: the first crash would persist through every later navigation,
   * because nothing in React resets boundary state on its own. RouteErrorBoundary below passes the
   * current path, so following a link out of a broken page renders the new page rather than the
   * fallback.
   *
   * Both optionals are written `| undefined` because exactOptionalPropertyTypes is on, and a caller
   * forwarding a possibly-absent value cannot assign it to a merely optional property.
   */
  resetKey?: string | undefined;
  title?: string | undefined;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // `override` on all four: the project sets noImplicitOverride, so a member that shadows one of
  // React.Component's has to say so. getDerivedStateFromError is exempt because it is static.
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the part that identifies which pane failed, and it is lost entirely
    // once the fallback renders, so it is written out before anything else happens.
    console.error("Unhandled render error", error, info.componentStack);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div className="stack" role="alert">
        <div className="notice notice-danger">
          <strong>{this.props.title ?? "This screen could not be displayed"}</strong>
          <p>
            Something on this page failed to load. The rest of the application is unaffected, so the
            navigation still works: move to another screen and come back to try this one again.
          </p>
          {/* The message is shown rather than hidden because the people using a sandbox are the
              people fixing it, and a reference they can paste is worth more than a tidy apology.
              It is the message only: a stack would leak file paths into the browser. */}
          <p className="muted">{error.message}</p>
          {/* Deliberately no retry button. src/interactionCoverage.test.ts holds every production
              module carrying an interactive control to declared browser evidence, and a boundary
              that only fires on an unhandled crash is close to impossible to exercise honestly in
              Playwright without a test-only route that crashes on purpose. Navigating away and back
              already remounts this pane through resetKey, so the retry exists; it just does not need
              a control of its own. Adding one means adding real E2E evidence with it. */}
        </div>
      </div>
    );
  }
}

/**
 * The boundary the shell uses, keyed on the current path.
 *
 * Reading the location here rather than in AppShell keeps the shell's hook list untouched, and means
 * any future placement inherits the reset behavior instead of having to remember it.
 */
export function RouteErrorBoundary({
  children,
  title,
}: {
  children: ReactNode;
  title?: string | undefined;
}) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname} title={title}>
      {children}
    </ErrorBoundary>
  );
}
